import { useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { BookOpen, ChevronDown, ChevronRight, Download, ExternalLink, Gamepad2, Search, X } from 'lucide-react'
import clsx from 'clsx'
import { getGameRegistry } from '@quebon/games'
import { UnitAssessmentSetsTab } from '@/components/admin/content/UnitAssessmentSetsTab'
import { DiagnosticSetsTab } from '@/components/admin/diagnostics/DiagnosticSetsTab'
import { commerceApi } from '@/api/commerce'
import { contentApi } from '@/api/content'
import { gameApi } from '@/api/games'
import { tenantApi } from '@/api/tenant'
import { TEXTBOOK_CATALOG } from '@/data/textbooks'
import type { ContentHierarchyNode, ContentHierarchyResponse } from '@/api/content'
import type { GameLinkedLesson } from '@/api/games'
import type { TextbookCatalogItem, TextbookKind } from '@/data/textbooks'
import type { GameEntry } from '@quebon/games'

const CONTENT_TABS = ['curriculum', 'textbook', 'specialLecture', 'diagnostic', 'gameMath'] as const
type ContentTab = typeof CONTENT_TABS[number]

function parseContentTab(value: string | null): ContentTab {
  return CONTENT_TABS.includes(value as ContentTab) ? value as ContentTab : 'curriculum'
}

export default function ContentManagement() {
  const { t } = useTranslation('admin')
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseContentTab(searchParams.get('tab'))
  const selectTab = (nextTab: ContentTab) => {
    if (nextTab === 'curriculum') {
      setSearchParams({}, { replace: true })
      return
    }
    setSearchParams({ tab: nextTab }, { replace: true })
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6 text-gray-900">
        {t('contentManagement.title')}
      </h1>
      <div className="flex gap-2 mb-6 border-b">
        <TabButton active={tab === 'curriculum'} onClick={() => selectTab('curriculum')}>
          {t('contentManagement.tabs.curriculum')}
        </TabButton>
        <TabButton active={tab === 'textbook'} onClick={() => selectTab('textbook')}>{t('contentManagement.tabs.textbook')}</TabButton>
        <TabButton active={tab === 'specialLecture'} onClick={() => selectTab('specialLecture')}>{t('contentManagement.tabs.specialLecture')}</TabButton>
        <TabButton active={tab === 'diagnostic'} onClick={() => selectTab('diagnostic')}>{t('diagnosticSets.tabLabel')}</TabButton>
        <TabButton active={tab === 'gameMath'} onClick={() => selectTab('gameMath')}>
          {t('contentManagement.tabs.gameMath')}
        </TabButton>
      </div>
      {tab === 'curriculum'
        ? <CurriculumTab />
        : tab === 'textbook'
          ? <TextbookTab />
          : tab === 'specialLecture'
            ? <UnitAssessmentSetsTab />
            : tab === 'diagnostic'
              ? <DiagnosticSetsTab />
              : <GameMathTab />}
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 -mb-px text-sm font-medium ${active ? 'border-b-2 border-primary-600 text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
    >
      {children}
    </button>
  )
}

interface PurchasedProduct {
  id: number
  name: string | null
  contentIds: number[]
}

const PROCESS_NAME_COLLATOR = new Intl.Collator('en', {
  numeric: true,
  sensitivity: 'base',
})

function comparePurchasedProducts(first: PurchasedProduct, second: PurchasedProduct): number {
  const firstName = first.name?.trim()
  const secondName = second.name?.trim()

  if (firstName && secondName) {
    return PROCESS_NAME_COLLATOR.compare(firstName, secondName) || first.id - second.id
  }
  if (firstName) return -1
  if (secondName) return 1
  return first.id - second.id
}

interface LcCurriculumChapter {
  id: number
  sequenceOrder: number
  title: string
  lessons: ContentHierarchyNode[]
}

interface LcCurriculumCourse {
  id: number
  title: string
  sourceCurriculumId: number | null
  sourceCurriculumTitle: string
  chapters: LcCurriculumChapter[]
}

const EMPTY_CONTENT_IDS: number[] = []

function displayTitle(content: Pick<ContentHierarchyNode, 'title' | 'bareTitle'>): string {
  return content.bareTitle?.trim() || content.title
}

function toCurriculumCourse(
  course: ContentHierarchyNode | ContentHierarchyResponse,
  sourceCurriculumId: number | null,
  sourceCurriculumTitle: string,
): LcCurriculumCourse {
  return {
    id: course.id,
    title: displayTitle(course),
    sourceCurriculumId,
    sourceCurriculumTitle,
    chapters: course.children
      .filter((chapter) => chapter.type === 'module')
      .map((chapter) => ({
        id: chapter.id,
        sequenceOrder: chapter.sequenceOrder,
        title: displayTitle(chapter),
        lessons: chapter.children.filter((lesson) => lesson.type === 'lesson'),
      })),
  }
}

function getCoursesFromHierarchy(hierarchy: ContentHierarchyResponse): LcCurriculumCourse[] {
  const sourceCurriculumTitle = displayTitle(hierarchy)
  if (hierarchy.type === 'course') {
    return [toCurriculumCourse(hierarchy, null, sourceCurriculumTitle)]
  }

  return hierarchy.children
    .filter((child) => child.type === 'course')
    .map((course) => toCurriculumCourse(course, hierarchy.id, sourceCurriculumTitle))
}

function curriculumCourseKey(course: LcCurriculumCourse): string {
  return `${course.sourceCurriculumId}:${course.id}`
}

function CurriculumTab() {
  const { t } = useTranslation('admin')
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [selectedCourseKey, setSelectedCourseKey] = useState<string | null>(null)

  const purchasedProductsQuery = useQuery({
    queryKey: ['lc-admin', 'content-management', 'purchased-products'],
    queryFn: async (): Promise<PurchasedProduct[]> => {
      const [subscriptionsResponse, products] = await Promise.all([
        tenantApi.getSubscriptions(),
        commerceApi.getProducts(),
      ])
      const subscriptions = (subscriptionsResponse.data.data ?? [])
        .filter((subscription) => subscription.status.toLowerCase() === 'active')
      const productsById = new Map(products.map((product) => [product.id, product]))
      const missingProductIds = [...new Set(
        subscriptions
          .map((subscription) => subscription.productId)
          .filter((productId) => !productsById.has(productId)),
      )]
      const missingProducts = await Promise.all(
        missingProductIds.map((productId) => commerceApi.getProduct(productId).catch(() => null)),
      )
      missingProducts.forEach((product) => {
        if (product) productsById.set(product.id, product)
      })

      const purchasedProductsById = new Map<number, PurchasedProduct>()
      subscriptions.forEach((subscription) => {
        if (purchasedProductsById.has(subscription.productId)) return

        const product = productsById.get(subscription.productId)
        const productContentIds = product?.contents.map((content) => content.contentId) ?? []
        const fallbackContentIds = subscription.linkedContentId == null
          ? []
          : [subscription.linkedContentId]

        purchasedProductsById.set(subscription.productId, {
          id: subscription.productId,
          name: subscription.productName ?? product?.name ?? null,
          contentIds: [...new Set(productContentIds.length > 0 ? productContentIds : fallbackContentIds)],
        })
      })

      return [...purchasedProductsById.values()].sort(comparePurchasedProducts)
    },
    staleTime: 30 * 1000,
  })

  const purchasedProducts = purchasedProductsQuery.data ?? []
  const selectedProduct = purchasedProducts.find((product) => product.id === selectedProductId)
    ?? purchasedProducts[0]
  const selectedContentIds = selectedProduct?.contentIds ?? EMPTY_CONTENT_IDS

  const hierarchyQuery = useQuery({
    queryKey: ['lc-admin', 'content-management', 'product-hierarchies', selectedProduct?.id, selectedContentIds],
    queryFn: () => Promise.all(selectedContentIds.map((contentId) => contentApi.getContentHierarchy(contentId))),
    enabled: selectedContentIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const courses = (hierarchyQuery.data ?? []).flatMap(getCoursesFromHierarchy)
  const selectedCourse = courses.find(
    (course) => curriculumCourseKey(course) === selectedCourseKey,
  ) ?? courses[0]

  const selectProduct = (product: PurchasedProduct) => {
    setSelectedProductId(product.id)
    setSelectedCourseKey(null)
  }

  return (
    <section className="space-y-4" aria-label={t('contentManagement.curriculum.sectionLabel')}>
      <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-20 text-xs font-semibold text-gray-500">{t('contentManagement.curriculum.process')}</span>
          {purchasedProductsQuery.isLoading && (
            <span className="text-sm text-gray-500">{t('contentManagement.curriculum.processLoading')}</span>
          )}
          {purchasedProducts.map((product) => (
            <button
              key={product.id}
              type="button"
              onClick={() => selectProduct(product)}
              aria-pressed={selectedProduct?.id === product.id}
              className={`rounded px-3 py-1.5 text-sm font-medium ${selectedProduct?.id === product.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {product.name ?? t('contentManagement.curriculum.processFallback', { id: product.id })}
            </button>
          ))}
          {purchasedProductsQuery.isError && (
            <span className="text-sm text-red-600">{t('contentManagement.curriculum.processLoadFailed')}</span>
          )}
          {!purchasedProductsQuery.isLoading && !purchasedProductsQuery.isError && purchasedProducts.length === 0 && (
            <span className="text-sm text-gray-500">{t('contentManagement.curriculum.processEmpty')}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
          <span className="w-20 text-xs font-semibold text-gray-500">{t('contentManagement.curriculum.curriculum')}</span>
          {hierarchyQuery.isLoading && (
            <span className="text-sm text-gray-500">{t('contentManagement.curriculum.curriculumLoading')}</span>
          )}
          {courses.map((course) => (
            <button
              key={curriculumCourseKey(course)}
              type="button"
              onClick={() => setSelectedCourseKey(curriculumCourseKey(course))}
              aria-pressed={selectedCourse
                ? curriculumCourseKey(selectedCourse) === curriculumCourseKey(course)
                : false}
              className={`rounded px-3 py-1.5 text-sm font-medium ${selectedCourse && curriculumCourseKey(selectedCourse) === curriculumCourseKey(course) ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <ContentMetadataText text={course.title} />
            </button>
          ))}
          {hierarchyQuery.isError && (
            <span className="text-sm text-red-600">{t('contentManagement.curriculum.curriculumLoadFailed')}</span>
          )}
          {selectedProduct && selectedContentIds.length === 0 && (
            <span className="text-sm text-gray-500">{t('contentManagement.curriculum.curriculumUnlinked')}</span>
          )}
          {selectedContentIds.length > 0 && !hierarchyQuery.isLoading && !hierarchyQuery.isError && courses.length === 0 && (
            <span className="text-sm text-gray-500">{t('contentManagement.curriculum.curriculumEmpty')}</span>
          )}
        </div>
      </div>

      {selectedCourse && (
        <CurriculumCourseTree
          key={curriculumCourseKey(selectedCourse)}
          course={selectedCourse}
        />
      )}
    </section>
  )
}

function CurriculumCourseTree({ course }: { course: LcCurriculumCourse }) {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const [lessonQuery, setLessonQuery] = useState('')
  const [expandedChapterIds, setExpandedChapterIds] = useState(
    () => new Set(course.chapters.map((chapter) => chapter.id)),
  )
  const lessonCount = course.chapters.reduce((sum, chapter) => sum + chapter.lessons.length, 0)
  const normalizedLessonQuery = lessonQuery.trim().toLocaleLowerCase()
  const isSearching = normalizedLessonQuery.length > 0
  const filteredChapters = isSearching
    ? course.chapters.flatMap((chapter) => {
        const lessons = chapter.lessons.filter((lesson) =>
          displayTitle(lesson).toLocaleLowerCase().includes(normalizedLessonQuery),
        )
        return lessons.length > 0 ? [{ ...chapter, lessons }] : []
      })
    : course.chapters
  const filteredLessonCount = filteredChapters.reduce((sum, chapter) => sum + chapter.lessons.length, 0)

  const toggleChapter = (chapterId: number) => {
    setExpandedChapterIds((previous) => {
      const next = new Set(previous)
      if (next.has(chapterId)) next.delete(chapterId)
      else next.add(chapterId)
      return next
    })
  }

  const openLesson = (moduleId: number, lessonId: number) => {
    const params = new URLSearchParams()
    if (course.sourceCurriculumId != null) {
      params.set('curriculumId', String(course.sourceCurriculumId))
    }
    params.set('courseId', String(course.id))
    params.set('moduleId', String(moduleId))
    navigate(`/admin/contents/lessons/${lessonId}?${params.toString()}`)
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="curriculum-tree">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
            <BookOpen className="size-4" aria-hidden />
          </span>
          <div>
          <h3 className="text-sm font-bold text-gray-900"><ContentMetadataText text={course.title} /></h3>
          <p className="mt-0.5 text-xs text-gray-500">{course.sourceCurriculumTitle}</p>
          </div>
        </div>
        <span className="text-xs text-gray-500">
          {t('contentManagement.curriculum.summary', { chapters: course.chapters.length, lessons: lessonCount })}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <label
          htmlFor={`lesson-search-${course.id}`}
          className="shrink-0 text-xs font-semibold text-gray-600"
        >
          {t('contentManagement.curriculum.lessonSearchLabel')}
        </label>
        <div className="relative min-w-56 max-w-lg flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            id={`lesson-search-${course.id}`}
            type="search"
            value={lessonQuery}
            onChange={(event) => setLessonQuery(event.target.value)}
            placeholder={t('contentManagement.curriculum.lessonSearchPlaceholder')}
            autoComplete="off"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          />
          {lessonQuery.length > 0 && (
            <button
              type="button"
              onClick={() => setLessonQuery('')}
              aria-label={t('contentManagement.curriculum.lessonSearchClear')}
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="size-4" aria-hidden />
            </button>
          )}
        </div>
        {isSearching && (
          <span className="text-xs font-medium text-primary-700" aria-live="polite">
            {t('contentManagement.curriculum.lessonSearchResult', { count: filteredLessonCount })}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2 p-3">
        {filteredChapters.map((chapter) => {
          const expanded = isSearching || expandedChapterIds.has(chapter.id)
          return (
            <div
              key={chapter.id}
              className={clsx(
                'overflow-hidden rounded-lg border transition-colors',
                expanded ? 'border-primary-200 bg-primary-50/30' : 'border-gray-200 bg-white',
              )}
            >
              <button
                type="button"
                onClick={() => toggleChapter(chapter.id)}
                aria-expanded={expanded}
                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-primary-50"
              >
                <span className="flex min-w-0 items-center gap-2">
                  {expanded
                    ? <ChevronDown className="size-4 shrink-0 text-primary-700" aria-hidden />
                    : <ChevronRight className="size-4 shrink-0 text-gray-500" aria-hidden />}
                  <span className="shrink-0 text-sm font-semibold text-gray-900">
                    {t('contentManagement.curriculum.chapterNumber', { number: chapter.sequenceOrder })}
                  </span>
                  <span className="truncate text-sm text-gray-600"><ContentMetadataText text={chapter.title} /></span>
                </span>
                <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                  {t('contentManagement.curriculum.lessonCount', { count: chapter.lessons.length })}
                </span>
              </button>
              {expanded && (
                <ul className="ml-5 flex flex-col gap-1 border-l border-primary-200 px-3 pb-3 pl-4">
                  {chapter.lessons.map((lesson) => (
                    <li key={lesson.id}>
                      <button
                        type="button"
                        onClick={() => openLesson(chapter.id, lesson.id)}
                        className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        <span className="flex h-6 min-w-10 items-center justify-center rounded-md bg-gray-100 px-1.5 text-xs font-semibold text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-700">
                          {chapter.sequenceOrder}-{lesson.sequenceOrder}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800 group-hover:text-primary-800">
                          {displayTitle(lesson)}
                        </span>
                        <ChevronRight className="size-4 shrink-0 text-gray-300 group-hover:text-primary-600" aria-hidden />
                      </button>
                    </li>
                  ))}
                  {chapter.lessons.length === 0 && (
                    <li className="px-3 py-3 text-sm text-gray-500">{t('contentManagement.curriculum.chapterLessonEmpty')}</li>
                  )}
                </ul>
              )}
            </div>
          )
        })}
        {isSearching && filteredChapters.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            {t('contentManagement.curriculum.lessonSearchEmpty', { keyword: lessonQuery.trim() })}
          </div>
        )}
      </div>
      {course.chapters.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-gray-500">
          {t('contentManagement.curriculum.lessonEmpty')}
        </div>
      )}
    </div>
  )
}

function GameMathTab() {
  const { t } = useTranslation('admin')
  const publishedGamesQuery = useQuery({
    queryKey: ['games', 'published', 'details'],
    queryFn: gameApi.getPublishedDetails,
  })
  const publishedGameByKey = new Map(
    (publishedGamesQuery.data ?? []).map((details) => [details.gameKey, details]),
  )
  const games = (getGameRegistry() as GameEntry[]).flatMap((game) => {
    const details = publishedGameByKey.get(game.id)
    return details ? [{ game, linkedLessons: details.linkedLessons }] : []
  })

  return (
    <section aria-label={t('contentManagement.gameMath.title')} className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            {t('contentManagement.gameMath.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('contentManagement.gameMath.description')}
          </p>
        </div>
        <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
          {t('contentManagement.gameMath.total', { total: games.length })}
        </span>
      </div>

      {publishedGamesQuery.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
          {t('contentManagement.gameMath.loading')}
        </div>
      ) : publishedGamesQuery.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-10 text-center text-sm text-red-700">
          {t('contentManagement.gameMath.loadFailed')}
        </div>
      ) : games.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
          {t('contentManagement.gameMath.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {games.map((game) => (
            <GameMathCard
              key={game.game.id}
              game={game.game}
              linkedLessons={game.linkedLessons}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function GameMathCard({
  game,
  linkedLessons,
}: {
  game: GameEntry
  linkedLessons: GameLinkedLesson[]
}) {
  const { t, i18n } = useTranslation('admin')
  const playUrl = `/admin/game-math/${encodeURIComponent(game.id)}`
  const localizedInfo = i18n.resolvedLanguage?.startsWith('en') ? game.en : game.ko

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="aspect-video overflow-hidden bg-gray-100">
        <img
          src={game.image}
          alt={localizedInfo.name}
          className="h-full w-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = 'none'
          }}
        />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold text-gray-900">{localizedInfo.name}</h3>
            <p className="mt-1 line-clamp-2 text-xs text-gray-600">{localizedInfo.desc}</p>
          </div>
          <Gamepad2 className="size-5 shrink-0 text-cyan-600" aria-hidden />
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-gray-700">
              {t('contentManagement.gameMath.linkedLessons')}
            </span>
            <span className="text-xs text-gray-500">
              {t('contentManagement.gameMath.linkedLessonTotal', { total: linkedLessons.length })}
            </span>
          </div>
          {linkedLessons.length === 0 ? (
            <p className="mt-2 text-xs text-gray-500">
              {t('contentManagement.gameMath.unlinked')}
            </p>
          ) : (
            <ul className="mt-2 max-h-32 space-y-2 overflow-y-auto">
              {linkedLessons.map((lesson, index) => (
                <li
                  key={`${lesson.curriculumTitle}:${lesson.courseTitle}:${lesson.moduleTitle}:${lesson.lessonTitle}:${index}`}
                  className="rounded-md bg-white px-2.5 py-2"
                >
                  <p className="flex flex-wrap items-center gap-1 text-xs font-medium text-gray-800">
                    <span><ContentMetadataText text={lesson.curriculumTitle} /></span>
                    <ChevronRight className="size-3 text-gray-300" aria-hidden />
                    <span><ContentMetadataText text={lesson.courseTitle} /></span>
                    <ChevronRight className="size-3 text-gray-300" aria-hidden />
                    <span><ContentMetadataText text={lesson.moduleTitle} /></span>
                    <ChevronRight className="size-3 text-gray-300" aria-hidden />
                    <span className="font-semibold text-gray-950"><ContentMetadataText text={lesson.lessonTitle} /></span>
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Link
          to={playUrl}
          className="mt-auto inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"
        >
          {t('contentManagement.gameMath.run')}
        </Link>
      </div>
    </article>
  )
}

function TextbookTab() {
  const { t } = useTranslation('admin')
  const [kind, setKind] = useState<TextbookKind>('checkup')
  const filtered = TEXTBOOK_CATALOG.filter((book) => book.kind === kind)

  return (
    <>
      <div className="mb-6 inline-flex rounded-xl bg-gray-100 p-1">
        <button
          type="button"
          onClick={() => setKind('checkup')}
          aria-pressed={kind === 'checkup'}
          className={clsx(
            'rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
            kind === 'checkup'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900',
          )}
        >
          {t('contentManagement.textbookCatalog.checkup')}
        </button>
        <button
          type="button"
          onClick={() => setKind('workbook')}
          aria-pressed={kind === 'workbook'}
          className={clsx(
            'rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
            kind === 'workbook'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900',
          )}
        >
          {t('contentManagement.textbookCatalog.workbook')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((book) => (
          <TextbookCard key={book.id} book={book} />
        ))}
      </div>
    </>
  )
}

function TextbookCard({ book }: { book: TextbookCatalogItem }) {
  const { t, i18n } = useTranslation('admin')
  const kindLabel = t(`contentManagement.textbookCatalog.${book.kind}`)
  const title = `${book.series} ${kindLabel}`
  const price = new Intl.NumberFormat(i18n.language === 'ko' ? 'ko-KR' : 'en-US').format(book.price)

  return (
    <article className="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="bg-gradient-to-b from-slate-50 to-slate-100 p-5">
        <img
          src={book.coverImage}
          alt={t('contentManagement.textbookCatalog.coverAlt', { title })}
          loading="lazy"
          className="mx-auto aspect-[210/297] w-full max-w-52 rounded-sm object-contain shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
        />
      </div>
      <div className="p-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <span className="mb-1.5 inline-block text-xs font-semibold text-primary-600">{kindLabel}</span>
            <h2 className="text-base font-bold text-gray-900">{title}</h2>
          </div>
          <p className="shrink-0 pt-5 text-sm font-bold text-gray-900">
            {t('contentManagement.textbookCatalog.price', { price })}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <a
            href={book.sampleUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          >
            <ExternalLink className="size-3.5" aria-hidden />
            {t('contentManagement.textbookCatalog.sampleView')}
          </a>
          <a
            href={book.sampleUrl}
            download={`${book.id}-sample.pdf`}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-primary-700"
          >
            <Download className="size-3.5" aria-hidden />
            {t('contentManagement.textbookCatalog.samplePdf')}
          </a>
        </div>
      </div>
    </article>
  )
}
